import {getTestProps} from '@benchling/js-test-utils';
import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Background,
  Connection,
  Edge,
  OnConnect,
  OnConnectStartParams,
  ReactFlow,
  useReactFlow,
} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import * as metrics from 'metrics/Metrics';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {isValidConnection} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/edges/connectionValidationUtils';
import {
  HandlesContext,
  HandlesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import {ProcessFlowchartTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  NodesContext,
  NodesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/NodesContext';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {parseHandleId} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {ControlledAutoLayoutButton} from 'ui-library/reactflow/controls/AutoLayoutControlButton';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {CustomMappingContext, CustomMappingContextValue} from './CustomMappingContext';
import {
  CustomMappingData,
  DEFAULT_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES,
  READ_ONLY_FLOWCHART_PROPS,
  SNAP_GRID,
  StaticTemplateFlowchart,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TransitionSource,
  UserFacingNodeTypes,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorLeftSidepanel, {
  LEFT_SIDEPANEL_WIDTH,
} from './ProcessFlowchartConfigEditorLeftSidepanel';
import {
  convertInteractiveFlowchartToSpecFlowchart,
  convertStaticFlowchartToSpecFlowchartWithTaskSchema,
} from './processFlowchartConfigEditorNetworkUtils';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNode,
} from './processFlowchartConfigEditorNodeTypes';
import {
  PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE,
} from './processFlowchartConfigEditorRegistry';
import ProcessFlowchartConfigEditorRightSidepanel from './ProcessFlowchartConfigEditorRightSidepanel';
import {
  FlowchartCustomMappingDataChangeType,
  FlowchartNodeChangeType,
  FlowchartOptions,
  FlowchartTransitionSourceChangeType,
} from './processFlowchartConfigEditorUtils';
import ProcessFlowchartValidationErrorBanner from './ProcessFlowchartValidationErrorBanner';
import useFlowchartState from './processInteractiveFlowchartChanges';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigWithPartialMetadata, LiveStateChanges} from './redux/FlowchartConfigStateManager';
import {FlowchartConfigAutosaverPausedCondition} from './redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from './SingletonTaskSchemasContext';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';
export interface ProcessFlowchartConfigEditorContentProps {
  flowchart: StaticTemplateFlowchart;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  allSchemasById: SchemaById;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  didInitialConfigHaveConditionalMapping: boolean;
  didInitialFlowchartHaveChainedRouters: boolean;
  refetchFlowchartConfig?: () => void;
  liveStateChanges: LiveStateChanges;
  locked: boolean;
  validationErrors: Array<ProcessFlowchartValidationError>;
  draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata;
  setDraftFlowchartConfigAndMetadata: (
    getNewFlowchartConfigAndMetadata: (
      currentFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
    ) => FlowchartConfigWithPartialMetadata,
    saveImmediately?: boolean
  ) => void;
}

function getAllowedNodeTypes(
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator
): Array<UserFacingNodeTypes> {
  const allowedTaskNodeTypes = compact(
    flowchartEditorConfigurator.allowedNodeExecutionTypes.map(
      (executionType) => PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE[executionType]
    )
  );
  const allowedNodeTypesForPlatformFeatures = compact(
    Object.entries(PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE)
      .filter(([feature]) =>
        flowchartEditorConfigurator.hasFeature(feature as ProcessFlowchartPlatformFeatureType)
      )
      .map(([_, nodeType]) => nodeType)
  );
  return [...allowedTaskNodeTypes, ...allowedNodeTypesForPlatformFeatures];
}

function getAdapterForNode(
  node: FlowchartNode | null
): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | null {
  if (node && isInteractiveTaskNode(node)) {
    return PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      node.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
  }
  return null;
}

/**
 * Horizontal flowchart config editor content that displays the specified flowchart config version.
 *
 * Converts static nodes and edges to interactive nodes and edges.
 */
export default function ProcessFlowchartConfigEditorContent(
  props: ProcessFlowchartConfigEditorContentProps
): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const interactionProps = props.locked ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;
  const singletonTaskSchemasByName = SingletonTaskSchemasContext.useContext();

  const allowedNodeTypes = useMemo(
    () => getAllowedNodeTypes(props.flowchartEditorConfigurator),
    [props.flowchartEditorConfigurator]
  );

  const [_isAutosaverPaused, setIsAutosaverPaused] = props.liveStateChanges.isAutosaverPausedState;

  const reactFlowInstance = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const addNodeParams = useMemo(
    () => ({
      flowchartEditorConfigurator: props.flowchartEditorConfigurator,
      rootTaskSchema: props.rootTaskSchema,
      taskDefinitionsForSidepanel: props.taskDefinitionsForSidepanel,
      didInitialFlowchartHaveChainedRouters: props.didInitialFlowchartHaveChainedRouters,
      singletonTaskSchemasByName,
    }),
    [
      props.flowchartEditorConfigurator,
      props.rootTaskSchema,
      props.taskDefinitionsForSidepanel,
      props.didInitialFlowchartHaveChainedRouters,
      singletonTaskSchemasByName,
    ]
  );

  const {
    interactiveNodes,
    interactiveEdges,
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    setNodesViaChangesForLayout,
    setEdgesViaChangesForLayout,
    // Sidepanel state
    isRightSidepanelOpen,
    selectedFieldId,
    selectedNodeIdForRightSidepanel,
    setSelectedFieldId,
    // Sidepanel functions
    handleRightSidepanelClose,
    openSidepanelForNode,
    openSidepanelForNodeWithField,
    getAddNodeOptionsForRightHandle,
    // Node addition state (handled in hook)
  } = useFlowchartState({
    staticFlowchart: props.flowchart,
    flowchartConfigMetadata: props.draftFlowchartConfigAndMetadata.metadata,
    validationErrors: props.validationErrors,
    setFlowchartAndMetadata: props.setDraftFlowchartConfigAndMetadata,
    addNodeParams,
  });

  const [connectionDragState, setConnectionDragState] =
    useState<HandlesContextValue['connectionDragState']>(null);

  const hasPerformedInitialAutolayout = useRef(false);
  const [performInitialFitView, setPerformInitialFitView] = useStateIfMounted(false);

  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(false);

  const interactiveFlowchartOptions: FlowchartOptions = useMemo(
    () => ({
      onNodeHeaderClick: openSidepanelForNode,
      onFieldClick: openSidepanelForNodeWithField,
      onUpdateNodeData: (nodeChangeGenerator) => {
        applyFlowchartChanges((flowchartConfig) => ({
          nodeChanges: nodeChangeGenerator(flowchartConfig),
        }));
      },
      getAddNodeOptionsForRightHandle,
      validationErrors: props.validationErrors,
    }),
    [
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      getAddNodeOptionsForRightHandle,
      applyFlowchartChanges,
      props.validationErrors,
    ]
  );

  // Trigger initial autolayout when nodes are first loaded (once only)
  useEffect(() => {
    // Only perform initial autolayout once, not every time nodes change
    if (interactiveNodes.length === 0 || hasPerformedInitialAutolayout.current) {
      return;
    }

    // Defer autolayout slightly to ensure that node dimensions have rendered and have dimensions.
    // If the nodes do not have dimensions, the layout algorithm falls back to nodes with no dimensions
    // which leads to an unreadable flowchart.
    setTimeout(() => {
      const flowchartHasNodesWithDimensions =
        interactiveNodes.length > 0 &&
        interactiveNodes.some(
          (node) => node.width != null && node.height != null && node.width > 0 && node.height > 0
        );

      const customLayoutSettings: Partial<LayoutSettings<FlowchartNode, Edge>> = {
        snapToGrid: SNAP_GRID,
      };
      if (!flowchartHasNodesWithDimensions) {
        // This is an unexpected state. If the nodes do not have computed dimensions, we use some defaults
        // which should produce a readable flowchart in most cases.
        // Since we do not have a good logging solution on the frontend we emit a metric to datadog to track
        // this case.
        metrics.count('benchling.processes.autolayout_flowchart_without_dimensions.count', 1);
        // This is a default height that we assume will produce a readable flowchart in most cases.
        // This may not result in the best results for flowcharts with lots of parallel branches and lots of
        // fields in each node.
        const fallbackNodeHeight = COMMON_NODE_WIDTH * 2;
        customLayoutSettings.calculateNodeDimensions = (node) => ({
          width: COMMON_NODE_WIDTH,
          height: fallbackNodeHeight,
        });
      }

      layoutFlowchart(
        {
          nodes: interactiveNodes,
          edges: interactiveEdges,
        },
        customLayoutSettings
      ).then(({nodes: layoutNodes, edges: layoutEdges}) => {
        setNodesViaChangesForLayout(layoutNodes);
        setEdgesViaChangesForLayout(layoutEdges);
        hasPerformedInitialAutolayout.current = true;
        setPerformInitialFitView(true);
      });
    }, 100);
  }, [
    interactiveNodes,
    interactiveEdges,
    setNodesViaChangesForLayout,
    setEdgesViaChangesForLayout,
    setPerformInitialFitView,
  ]);

  useEffect(() => {
    if (!performInitialFitView) {
      return;
    }
    setTimeout(() => {
      reactFlowInstance.fitView(DEFAULT_FIT_VIEW_OPTIONS);
    });
    setPerformInitialFitView(false);
  }, [performInitialFitView, reactFlowInstance, setPerformInitialFitView]);

  const handleConnectStart = useCallback(
    (event: React.MouseEvent | React.TouchEvent, params: OnConnectStartParams): void => {
      if (!params.handleId || !params.nodeId) {
        return;
      }

      const sourceHandleInfo = parseHandleId(params.handleId);
      setConnectionDragState({
        isDragging: true,
        sourceHandleInfo,
      });
    },
    []
  );

  const handleConnectEnd = useCallback((): void => {
    setConnectionDragState(null);
  }, []);
  const handleConnect: OnConnect = useCallback(
    (params) => {
      setConnectionDragState(null);

      if (
        params.source == null ||
        params.target == null ||
        params.sourceHandle == null ||
        params.targetHandle == null
      ) {
        return;
      }
      onConnect(params);
    },
    [onConnect]
  );

  const dragOverHandler = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const addNodeToCanvas = useCallback(
    (newNode: FlowchartNode): void => {
      applyFlowchartChanges(() => ({nodeChanges: [{type: 'add', item: newNode}], edgeChanges: []}));
    },
    [applyFlowchartChanges]
  );

  const hasPlaceholderNodes = useMemo(
    () => interactiveNodes.some((node) => isInteractivePlaceholderNode(node)),
    [interactiveNodes]
  );

  useEffect(() => {
    setIsAutosaverPaused(FlowchartConfigAutosaverPausedCondition.HAS_PLACEHOLDER_NODE, hasPlaceholderNodes);
  }, [hasPlaceholderNodes, setIsAutosaverPaused]);

  const taskDefinitionVersionBasedFlowchart = useMemo(
    () => convertInteractiveFlowchartToSpecFlowchart({nodes: interactiveNodes, edges: interactiveEdges}),
    [interactiveNodes, interactiveEdges]
  );

  // Memoize the selected node to prevent unnecessary recalculations
  const selectedNode = useMemo(
    () =>
      selectedNodeIdForRightSidepanel
        ? (interactiveNodes.find((node) => node.id === selectedNodeIdForRightSidepanel) ?? null)
        : null,
    [selectedNodeIdForRightSidepanel, interactiveNodes]
  );

  const handlesContext: HandlesContextValue = useMemo(
    () => ({
      handlesInUse: interactiveEdges.flatMap((edge) => {
        if (!edge.sourceHandle || !edge.targetHandle) {
          throw new Error('Invalid edge: missing sourceHandle or targetHandle');
        }
        return [edge.sourceHandle, edge.targetHandle];
      }),
      connectionDragState,
    }),
    [interactiveEdges, connectionDragState]
  );

  const validateConnection = useCallback(
    (connection: Connection): boolean => {
      const getAdapterForNodeId = (
        nodeId: string
      ): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | null => {
        const node = interactiveNodes.find((n) => n.id === nodeId) ?? null;
        return getAdapterForNode(node);
      };

      const getNodeData = (nodeId: string): {nodeDetailsType?: ProcessFlowchartNodeDetailsType} | null => {
        const node = interactiveNodes.find((n) => n.id === nodeId);
        if (!node) {
          return null;
        }

        if (isInteractiveTaskNode(node)) {
          return {nodeDetailsType: node.data.nodeDetailsType};
        }
        return null;
      };

      return isValidConnection(connection, interactiveEdges, getNodeData, getAdapterForNodeId);
    },
    [interactiveEdges, interactiveNodes]
  );

  const nodesContext: NodesContextValue = useMemo(
    () => ({
      nodes: interactiveNodes,
      startNode:
        interactiveNodes.find(
          (node): node is TaskSchemaNode =>
            isInteractiveTaskNode(node) &&
            node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
        ) ?? null,
      endNode:
        interactiveNodes.find(
          (node): node is TaskSchemaNode =>
            isInteractiveTaskNode(node) &&
            node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
        ) ?? null,
    }),
    [interactiveNodes]
  );

  const customMappingContext: CustomMappingContextValue = props.customMappingDataByEdgeId;

  // Updating custom mapping data for an edge will automatically update lookup config through the context
  const onChangeCustomMappingData = useCallback(
    (edgeId, newCustomMappingData) => {
      applyFlowchartChanges((flowchartDraftState) => ({
        customMappingChanges: [
          {
            type: FlowchartCustomMappingDataChangeType.REPLACE,
            id: edgeId,
            customMapping: newCustomMappingData,
          },
        ],
      }));
    },
    [applyFlowchartChanges]
  );

  const onChangeTransitionSource = useCallback(
    (nodeIdPair: string, newTransitionSource: TransitionSource) => {
      applyFlowchartChanges((flowchartDraftState) => ({
        transitionSourceChanges: [
          {
            type: FlowchartTransitionSourceChangeType.REPLACE,
            id: nodeIdPair,
            transitionSource: newTransitionSource,
          },
        ],
      }));
    },
    [applyFlowchartChanges]
  );

  /**
   * Enables updates to a node from the right sidepanel to be saved immediately to the DB
   * without waiting for an auto save.
   *
   * If isBlocking is true, a network call will be made immediately to save the flowchart config.
   * If isBlocking is false, the node changes will be accumulated with other changes to wait for auto save.
   */
  const onUpdateTaskDefinitionVersion = useCallback(
    (
      updater: (
        currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
      ) => TaskDefinitionVersionSpecWithTaskSchema<unknown>,
      isBlocking: boolean
    ) => {
      applyFlowchartChanges((flowchartDraftState) => {
        assert(
          selectedNodeIdForRightSidepanel != null,
          'Expected selectedNodeIdForRightSidepanel to be non-null'
        );

        const nodeToUpdate = flowchartDraftState.staticFlowchart.nodesStaticData.find(
          (node) => node.id === selectedNodeIdForRightSidepanel
        );
        if (nodeToUpdate == null) {
          return {};
        }
        assert(isStaticTaskSchemaNodeData(nodeToUpdate), 'Expected node to be a static task node');

        const currentFlowchart = convertStaticFlowchartToSpecFlowchartWithTaskSchema(
          flowchartDraftState.staticFlowchart
        );
        const newTaskDefinitionVersionSpec = updater(currentFlowchart);

        const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
          nodeToUpdate.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
        );
        return {
          nodeChanges: [
            {
              type: FlowchartNodeChangeType.REPLACE,
              id: selectedNodeIdForRightSidepanel,
              data: {
                ...nodeToUpdate,
                taskDefinitionVersionSpec: newTaskDefinitionVersionSpec,
                nodeName: adapter.getNodeName(
                  newTaskDefinitionVersionSpec,
                  nodeToUpdate.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
                ),
                sections: adapter.createSections(newTaskDefinitionVersionSpec, nodeToUpdate.nodeDetailsType),
              },
            },
          ],
        };
      });
    },
    [selectedNodeIdForRightSidepanel, applyFlowchartChanges]
  );

  return (
    <div
      className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContentWrapper)}
      {...getTestProps({component: 'ProcessFlowchartConfigEditorContent', element: 'root'})}
    >
      <ReactFlowWrapper fullWidth={true}>
        <div
          className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContent)}
          ref={reactFlowWrapper}
        >
          <ProcessFlowchartConfigEditorLeftSidepanel
            isOpen={isLeftSidepanelOpen}
            setIsOpen={setIsLeftSidepanelOpen}
            taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
            allowedNodeTypes={allowedNodeTypes}
            addNodeToCanvas={addNodeToCanvas}
            options={interactiveFlowchartOptions}
            rootTaskSchema={props.rootTaskSchema}
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
            reactFlowWrapper={reactFlowWrapper}
            metadataSidepanel={
              PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
                props.flowchartEditorConfigurator.flowchartTypeName
              ).metadataEditor
            }
          />
          <div
            style={{
              flexGrow: 1,
              marginLeft: FLOWCHART_SIDE_PANEL_WIDTH,
              position: 'relative',
            }}
          >
            <ProcessFlowchartValidationErrorBanner validationErrors={props.validationErrors} />
            <HandlesContext.Provider value={handlesContext}>
              <NodesContext.Provider value={nodesContext}>
                <CustomMappingContext.Provider value={customMappingContext}>
                  <ReactFlow
                    {...defaultReactFlowProps}
                    {...DEFAULT_FLOWCHART_PROPS}
                    {...interactionProps}
                    nodes={interactiveNodes}
                    nodeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES}
                    edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
                    edges={interactiveEdges}
                    onNodesChange={onReactFlowNodeChangesOnly}
                    onEdgesChange={onReactFlowEdgeChangesOnly}
                    onConnect={handleConnect}
                    onConnectStart={handleConnectStart}
                    onConnectEnd={handleConnectEnd}
                    onDragOver={dragOverHandler}
                    deleteKeyCode={['Backspace', 'Delete']}
                    isValidConnection={validateConnection}
                  >
                    <Background gap={SNAP_GRID} />
                    <HorizontalControls
                      shiftLeft={isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0}
                      additionalControls={
                        <ControlledAutoLayoutButton
                          flowchartData={{nodes: interactiveNodes, edges: interactiveEdges}}
                          customLayoutSettings={{snapToGrid: SNAP_GRID}}
                          setNodes={setNodesViaChangesForLayout}
                          setEdges={setEdgesViaChangesForLayout}
                        />
                      }
                    />
                    <DefaultBackground />
                  </ReactFlow>
                </CustomMappingContext.Provider>
              </NodesContext.Provider>
            </HandlesContext.Provider>
          </div>
          <ProcessFlowchartConfigEditorRightSidepanel
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
            selectedFieldId={selectedFieldId}
            onChangeSelectedFieldId={setSelectedFieldId}
            interactiveFlowchart={{nodes: interactiveNodes, edges: interactiveEdges}}
            flowchart={taskDefinitionVersionBasedFlowchart}
            selectedNode={selectedNode}
            size={
              getAdapterForNode(selectedNode)?.getDisplayOptions().rightSidepanelSize ?? SidepanelSize.MEDIUM
            }
            isOpen={isRightSidepanelOpen}
            onClose={handleRightSidepanelClose}
            onUpdateTaskDefinitionVersion={onUpdateTaskDefinitionVersion}
            interactiveFlowchartOptions={interactiveFlowchartOptions}
            didInitialConfigHaveConditionalMapping={props.didInitialConfigHaveConditionalMapping}
            didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
            allSchemasById={props.allSchemasById}
            flowchartTaskSchema={props.rootTaskSchema}
            customMappingDataByEdgeId={props.customMappingDataByEdgeId}
            onChangeCustomMappingData={onChangeCustomMappingData}
            transitionSourceByNodeIdPair={props.transitionSourceByNodeIdPair}
            onChangeTransitionSource={onChangeTransitionSource}
            applyFlowchartChanges={applyFlowchartChanges}
            refetchFlowchartConfig={props.refetchFlowchartConfig}
            platformFlowchartFeatures={props.flowchartEditorConfigurator.platformFlowchartFeatures}
            liveStateChanges={props.liveStateChanges}
            locked={props.locked}
            taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
            validationErrors={props.validationErrors}
          />
        </div>
      </ReactFlowWrapper>
    </div>
  );
}
